import React from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { Box, Typography } from '@mui/material';

/**
 * 외국인 환자 통계 차트 컴포넌트
 *
 * 백엔드 stats_service 가 생성한 chart_data 를 받아 Highcharts 로 렌더링합니다.
 * chart_data 구조:
 *   {
 *     type: 'column' | 'bar' | 'line' | 'pie',
 *     title: string,
 *     categories: string[],   // x축 (pie 는 불필요)
 *     series: [{ name, data }]
 *   }
 */
const StatsChart = ({ chartData }) => {
  if (!chartData || !chartData.type) return null;

  const { type, title, categories, series } = chartData;

  // ── 파이 차트 ──────────────────────────────────────────
  if (type === 'pie') {
    const pieData = series && series.length > 0 ? series[0].data : [];

    const options = {
      chart: { type: 'pie', height: 420 },
      title: {
        text: title || '',
        style: { fontSize: '15px', fontWeight: 'bold', color: '#333' },
      },
      plotOptions: {
        pie: {
          allowPointSelect: true,
          cursor: 'pointer',
          dataLabels: {
            enabled: true,
            format: '<b>{point.name}</b><br/>{point.percentage:.1f}%<br/>({point.y:,.0f}명)',
            style: { fontSize: '11px' },
          },
          showInLegend: true,
        },
      },
      legend: { align: 'center', verticalAlign: 'bottom', layout: 'horizontal' },
      tooltip: {
        pointFormat:
          '<span style="color:{point.color}">●</span> {series.name}: <b>{point.y:,.0f}명</b> ({point.percentage:.1f}%)',
      },
      series: [{ name: '환자수', colorByPoint: true, data: pieData }],
      credits: { enabled: false },
    };

    return (
      <Box sx={{ mt: 2 }}>
        <HighchartsReact highcharts={Highcharts} options={options} />
      </Box>
    );
  }

  // ── 컬럼 / 바 / 라인 차트 ──────────────────────────────
  const isLine = type === 'line';

  const options = {
    chart: { type: type === 'bar' ? 'bar' : isLine ? 'line' : 'column', height: 420 },
    title: {
      text: title || '',
      style: { fontSize: '15px', fontWeight: 'bold', color: '#333' },
    },
    xAxis: {
      categories: categories || [],
      labels: { style: { fontSize: '12px' } },
    },
    yAxis: {
      title: { text: '환자수 (명)' },
      labels: {
        formatter: function () {
          if (this.value >= 1000000) return (this.value / 1000000).toFixed(1) + 'M';
          if (this.value >= 1000) return (this.value / 1000).toFixed(0) + 'K';
          return Highcharts.numberFormat(this.value, 0, '.', ',');
        },
      },
    },
    tooltip: {
      shared: true,
      formatter: function () {
        let s = '<b>' + this.x + '</b><br/>';
        (this.points || [this]).forEach((pt) => {
          s +=
            '<span style="color:' +
            pt.color +
            '">●</span> ' +
            pt.series.name +
            ': <b>' +
            Highcharts.numberFormat(pt.y, 0, '.', ',') +
            '명</b><br/>';
        });
        return s;
      },
    },
    plotOptions: {
      column: { dataLabels: { enabled: false } },
      bar: { dataLabels: { enabled: false } },
      line: { marker: { enabled: true, radius: 4 }, dataLabels: { enabled: false } },
    },
    series: series || [],
    legend: { align: 'center', verticalAlign: 'bottom' },
    credits: { enabled: false },
  };

  return (
    <Box sx={{ mt: 2 }}>
      <HighchartsReact highcharts={Highcharts} options={options} />
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, textAlign: 'right' }}>
        출처: 한국보건산업진흥원 외국인환자 유치 실적
      </Typography>
    </Box>
  );
};

export default StatsChart;
